import { AccordionButton } from '@shared/buttons/accordion';
import { useGetIconOptimized } from '@shared/hooks/useGetIconOptimized';
import { ModalBasic } from '@shared/ui/modal/ModalBasic';
import { isDefaultView } from '@shared/utils/applications';
import { useTakeApplicationItemsMutation } from '@store/api/applications.api';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectSelectedApplications } from '@store/slices/applications/selectors';
import { applicationsActions } from '@store/slices/applications/slice';
import { AccordionApplicationCardItemsTitle } from '@widgets/cabinet/applicationCard/AccordionApplicationCardItemsTitle';
import { ApplicationCardAddress } from '@widgets/cabinet/applicationCard/ApplicationCardAddress';
import { ApplicationCardItemDetails } from '@widgets/cabinet/applicationCard/ApplicationCardItemDetails';
import { ApplicationCardRemovableItem } from '@widgets/cabinet/applicationCard/ApplicationCardRemovableItem';
import { ApplicationCardWhoNeeds } from '@widgets/cabinet/applicationCard/ApplicationCardWhoNeeds';
import { Application } from '@widgets/cabinet/applicationCard/types';
import { useContactRequestData } from '@widgets/profile/useContactRequestData';
import * as React from 'react';
import { SuccessHandToHandButtonToken } from '../SuccessHandToHandButtonToken';
import { useNotify } from '@shared/hooks/useNotify';
import { useDispatch } from 'react-redux';

interface ApplicationConfirmModalProps {
  isVisible: boolean;
  onClose: (successToken?: SuccessHandToHandButtonToken) => void;
}

function useWidgetState({ isVisible, onClose }: ApplicationConfirmModalProps) {
  const dispatch = useAppDispatch();
  const selectedApplications = useAppSelector(selectSelectedApplications);
  const { mySendContactRequests } = useContactRequestData();
  const imageHeading = { optimized: useGetIconOptimized('circle-girl-with-hands') };

  const [mutation, { isLoading }] = useTakeApplicationItemsMutation();
  const notify = useNotify();

  const takeApplicationItems = React.useCallback(async () => {
    try {
      await mutation().unwrap();

      const customerIds = selectedApplications.map((app) => app.customer?.id).filter(Boolean);
      const sentCustomersIds = mySendContactRequests?.items.map((cr) => cr.userInNeed.id) || [];

      const needToSendContactRequest = customerIds.find((id) => !sentCustomersIds.includes(id));

      if (!needToSendContactRequest) {
        dispatch(applicationsActions.removeAll());
      }

      onClose(
        needToSendContactRequest ? SuccessHandToHandButtonToken.SendContactRequest : SuccessHandToHandButtonToken.Info,
      );
    } catch (e: any) {
      if (e.data === 'errors.too-many-requests')
        notify('errors.too-many-requests', {
          autoClose: 2000,
        });
    }
  }, [mySendContactRequests, selectedApplications]);

  return {
    isVisible,
    imageHeading,
    applications: selectedApplications,
    onClose,
    isLoading,
    onApprove: takeApplicationItems,
  } as const;
}

export const ApplicationConfirmModal: React.FC<ApplicationConfirmModalProps> = (props) => {
  const { isVisible, imageHeading, applications, onClose, isLoading, onApprove } = useWidgetState(props);

  return (
    <ModalBasic
      isVisible={isVisible}
      title="please-confirm"
      imageHeading={imageHeading}
      secondaryBtnLabel="cancel-2"
      approveBtnLabel="confirm"
      onClose={onClose}
      onApprove={onApprove}
      isLoading={isLoading}
      contentClassName="overflow-y-auto max-h-70vh md:max-h-60vh"
    >
      {applications.map((app) => (
        <AccordionApplicationView {...app} defaultCollapsed key={app.id} />
      ))}
    </ModalBasic>
  );
};

type AccordionApplicationViewProps = Application & {
  defaultCollapsed: boolean;
};

const AccordionApplicationView = (props: AccordionApplicationViewProps) => {
  const { customer, items, view, defaultCollapsed } = props;
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const dispatch = useDispatch();

  const ApplicationCardItem = isDefaultView(view) ? ApplicationCardRemovableItem : ApplicationCardItemDetails;
  return (
    <AccordionButton
      wrapperClassName="mb-2"
      defaultCollapsed={defaultCollapsed}
      summary={<ApplicationCardAddress className="text-black" address={customer.address} />}
      size="big"
      children={
        <div className="flex flex-col gap-y-6">
          <ApplicationCardWhoNeeds customer={customer} items={items} view={view} className="text-xs" withoutClamp />

          <div>
            <AccordionApplicationCardItemsTitle view={view} isCollapsed={isCollapsed} onClick={setIsCollapsed} />
            {isCollapsed &&
              items.map((item) => (
                <ApplicationCardItem
                  key={item.id}
                  {...item}
                  needConfirm
                  onRemove={(id) =>
                    dispatch(applicationsActions.toggleItemIsChecked({ application: props, itemId: id }))
                  }
                />
              ))}
          </div>
        </div>
      }
    />
  );
};
